import type { EventDto } from '@/types/dtos/events/response/event.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { RoleSlotDto } from '@/types/dtos/contributors/response/contributor.dto';
import { addDays, format } from 'date-fns';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import {
  getFlowSlotTimeRangeOnly,
  getFlowSlotTiming,
  getSegmentedSessionTimeRange,
} from '@/components/events/helpers/get-flow-day-number.helper';
import { isSegmentedSession } from '@/components/flows/detail/slot-list/session-segment.helpers';
import { resolveFlowEntityIdForPlayers } from '@/helpers/object.id.helper';

const formatCompactSlotLabel = (start: string, end?: string) =>
  end != null ? `Slot: ${start} - ${end}` : `Slot: ${start}`;

const flowMatchesContext = (flow: FlowDto | undefined, flowContext: FlowDto) => {
  if (!flow) return false;
  return resolveFlowEntityIdForPlayers(flow) === resolveFlowEntityIdForPlayers(flowContext);
};

export const getRoleSlotDescription = (
  roleSlot: RoleSlotDto,
  event?: EventDto,
  eventFlows?: FlowDto[],
  flowContext?: FlowDto | null,
): string | undefined => {
  if (!event || !eventFlows) return undefined;

  const normalizeId = (value?: string | { id?: string } | { _id?: string } | { $oid?: string } | unknown) => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value) {
      if ('id' in value && typeof (value as { id?: string }).id === 'string') return (value as { id: string }).id;
      if ('_id' in value && typeof (value as { _id?: string })._id === 'string') return (value as { _id: string })._id;
      if ('$oid' in value && typeof (value as { $oid?: string }).$oid === 'string') {
        return (value as { $oid: string }).$oid;
      }
    }
    if (typeof (value as { toString?: () => string }).toString === 'function') {
      return (value as { toString: () => string }).toString();
    }
    return undefined;
  };

  const getEventSlotByIdOrOrigin = (eventSlotId?: string) => {
    if (!eventSlotId) return undefined;
    return event.slots.find((slot) => {
      const slotId = normalizeId(slot.id);
      const slotOrigin = normalizeId(slot.origin);
      return slotId === eventSlotId || slotOrigin === eventSlotId;
    });
  };

  if (roleSlot.entityType === 'FlowSlot') {
    const entityId = typeof roleSlot.entity === 'string' ? roleSlot.entity : roleSlot.entity.id;
    const flow = eventFlows.find((flow) => flow.slots.find((slot) => slot.id === entityId));

    const normalizedEventSlotId = normalizeId(roleSlot.eventSlotId as unknown as string);
    const flowSlotInEvent = normalizedEventSlotId
      ? getEventSlotByIdOrOrigin(normalizedEventSlotId)
      : event.slots.find((slot) => slot.content === flow?.id || slot.content === flow?.origin);

    const contentSlotInFlow = flow?.slots.find((slot) => slot.id === entityId);

    if (flowSlotInEvent && contentSlotInFlow && flow) {
      if (flowContext && flowMatchesContext(flow, flowContext)) {
        const range = isSegmentedSession(flow, contentSlotInFlow)
          ? getSegmentedSessionTimeRange(event, flowSlotInEvent, flow, contentSlotInFlow)
          : getFlowSlotTimeRangeOnly(event, flowSlotInEvent, contentSlotInFlow);
        if (range) {
          return formatCompactSlotLabel(range.start, range.end);
        }
      }
      const timingInfo = getFlowSlotTiming(event, flowSlotInEvent, flow, contentSlotInFlow);
      return `in ${contentSlotInFlow.title ?? flow.title} (${timingInfo})`;
    }
  }

  if (roleSlot.entityType === 'Flow') {
    const flowId = typeof roleSlot.entity === 'string' ? roleSlot.entity : roleSlot.entity.id;
    const flow = eventFlows.find((item) => item.id === flowId || item.origin === flowId);
    if (!flow) return undefined;

    const normalizedEventSlotId = normalizeId(roleSlot.eventSlotId as unknown as string);
    const flowSlotInEvent = normalizedEventSlotId
      ? getEventSlotByIdOrOrigin(normalizedEventSlotId)
      : event.slots.find((slot) => slot.content === flow.id || slot.content === flow.origin);

    if (!flowSlotInEvent) return undefined;

    const day = event.days.find((eventDay) => eventDay.id === flowSlotInEvent.day.id);
    if (!day) return undefined;

    const dayNumber = event.days.indexOf(day) + 1;
    const dateLabel = format(addDays(new Date(event.startDate), dayNumber - 1), 'dd.MM.');

    const startOffsetMinutes = flowSlotInEvent.startOffsetMinutes;
    const slotStartTime = minuteOffsetToTime(startOffsetMinutes, event.startDate);

    const durationMinutes =
      flowSlotInEvent.durationPlannedMinutes ?? flow.durationPlannedMinutes ?? flow.computedDurationMinutes;

    if (flowContext && flowMatchesContext(flow, flowContext)) {
      if (durationMinutes === undefined) {
        return formatCompactSlotLabel(slotStartTime);
      }
      const endOffsetMinutes = startOffsetMinutes + durationMinutes;
      const slotEndTime = minuteOffsetToTime(endOffsetMinutes, event.startDate);
      return formatCompactSlotLabel(slotStartTime, slotEndTime);
    }

    if (durationMinutes === undefined) {
      return `Day ${dayNumber} | ${dateLabel} | ${slotStartTime}`;
    }

    const endOffsetMinutes = startOffsetMinutes + durationMinutes;
    const slotEndTime = minuteOffsetToTime(endOffsetMinutes, event.startDate);

    return `Day ${dayNumber} | ${dateLabel} | ${slotStartTime} - ${slotEndTime}`;
  }

  return undefined;
};
